<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Certificate Verification</h1>
        <p class="text-gray-600">Verify the authenticity of this certificate</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-lg shadow-sm border p-8 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Verifying certificate...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-white rounded-lg shadow-sm border p-8 text-center">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Verification Failed</h2>
        <p class="text-gray-600 mb-4">{{ error }}</p>
        <button @click="goHome" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Go Home
        </button>
      </div>

      <!-- Certificate Details -->
      <div v-else-if="certificateData" class="bg-white rounded-lg shadow-sm border overflow-hidden">
        <!-- Success Header -->
        <div class="bg-green-50 border-b border-green-200 px-6 py-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-3">
              <h2 class="text-lg font-semibold text-green-900">Certificate Verified</h2>
              <p class="text-green-700">This certificate is authentic and has been verified</p>
            </div>
          </div>
        </div>

        <!-- Certificate Information -->
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div>
                <h3 class="text-sm font-medium text-gray-500">Certificate Number</h3>
                <p class="text-lg font-semibold text-gray-900">{{ certificateData.certificateNumber }}</p>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-500">Student Name</h3>
                <p class="text-lg font-semibold text-gray-900">{{ certificateData.studentName }}</p>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-500">Course Name</h3>
                <p class="text-lg font-semibold text-gray-900">{{ certificateData.courseName }}</p>
              </div>
            </div>
            <div class="space-y-4">
              <div>
                <h3 class="text-sm font-medium text-gray-500">Institution</h3>
                <p class="text-lg font-semibold text-gray-900">{{ certificateData.institutionName }}</p>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-500">CPD Points</h3>
                <p class="text-lg font-semibold text-gray-900">{{ certificateData.cpdPoints }} Points</p>
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-500">Completion Date</h3>
                <p class="text-lg font-semibold text-gray-900">{{ formatDate(certificateData.completionDate) }}</p>
              </div>
            </div>
          </div>

          <!-- Verification Details -->
          <div class="mt-8 pt-6 border-t border-gray-200">
            <h3 class="text-sm font-medium text-gray-500 mb-2">Verification Details</h3>
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="font-medium text-gray-700">Verified on:</span>
                  <span class="text-gray-900 ml-2">{{ formatDate(new Date().toISOString()) }}</span>
                </div>
                <div>
                  <span class="font-medium text-gray-700">Verification ID:</span>
                  <span class="text-gray-900 ml-2">{{ certificateData.certificateNumber }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="mt-8 flex justify-center">
            <button @click="goHome" class="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
              Go Home
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import api from '../../services/api';

export default {
  name: 'CertificateVerification',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const loading = ref(true);
    const error = ref('');
    const certificateData = ref(null);

    const parseCertificateData = async () => {
      try {
        // Extract parameters from query
        const certParam = route.query.cert || route.query.certificate_serial_number;
        const tokenParam = route.query.token || route.query.user_id;

        if (!certParam || !tokenParam) {
          throw new Error('Missing certificate or token parameters');
        }

        console.log('Certificate params:', { certParam, tokenParam });

        // Fetch certificate data from API using cert number and user_id
        const response = await api.verifyCertificate(tokenParam, certParam);

        console.log('API Response:', response);

        // Check if certificate is valid
        if (!response.valid) {
          throw new Error('Certificate verification failed');
        }

        // Transform API response to match expected format
        const certificateData = {
          certificateNumber: response.certificate_serial_number,
          studentName: response.user?.full_name || 'N/A',
          courseName: response.assessment?.assessment_name || 'N/A',
          institutionName: response.assessment?.facility_name || 'N/A',
          cpdPoints: response.completion?.cpd_points_earned || 0,
          completionDate: response.completion?.completion_date || '',
          token: tokenParam
        };

        console.log('Fetched certificate data:', certificateData);
        return certificateData;

      } catch (err) {
        console.error('Error fetching certificate data:', err);
        console.error('Error details:', err.response?.data || err.message);
        throw new Error('Invalid certificate parameters or verification failed');
      }
    };

    const formatDate = (dateString) => {
      try {
        return new Date(dateString).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return dateString;
      }
    };

    const goHome = () => {
      router.push('/');
    };


    onMounted(async () => {
      try {
        certificateData.value = await parseCertificateData();
        loading.value = false;
        toast.success('Certificate verified successfully');
      } catch (err) {
        error.value = err.message || 'Failed to verify certificate';
        loading.value = false;
        toast.error(error.value);
      }
    });

    return {
      loading,
      error,
      certificateData,
      formatDate,
      goHome
    };
  }
};
</script>

<style scoped>
@media print {
  .bg-gray-50,
  .bg-white {
    background: white !important;
  }
  
  button {
    display: none !important;
  }
}
</style>
